import React__default from "react";
import ReactDOM__default from "react-dom";
import { offset } from "../../helpers/helper/index.mjs";
import debounce from "debounce";
const EqualHeightContext = React__default.createContext({
  addElementToGroup: (
    /* istanbul ignore next */
    () => null
  ),
  groupHeights: {},
  triggerUpdate: (
    /* istanbul ignore next */
    () => null
  )
});
const EqualHeightProvider = (props) => {
  const { children, groups, offSet = 3, onGroupHeightsChange } = props;
  const elementGroups = React__default.useRef({});
  const [groupHeights, setGroupHeights] = React__default.useState({});
  const updateGroupHeights = React__default.useCallback((group, heights) => {
    ReactDOM__default.flushSync(() => {
      setGroupHeights((prevState) => ({
        ...prevState,
        [group]: heights
      }));
    });
  }, []);
  const setGroupHeight = React__default.useCallback(
    (groupKeys) => {
      const keys = [...groupKeys];
      const group = keys.shift();
      if (!group) {
        return;
      }
      if (!elementGroups.current[group]) {
        elementGroups.current[group] = [];
      }
      const elementGroup = elementGroups.current[group];
      const elementOffsetGroup = splitElementsByOffset(elementGroup, offSet);
      const heightsByElements = [];
      for (const key of Object.keys(elementOffsetGroup)) {
        heightsByElements.push({
          elements: elementOffsetGroup[key],
          height: findTallestChildHeight(elementOffsetGroup[key]),
          offsetTop: Number(key)
        });
      }
      setTimeout(() => {
        updateGroupHeights(group, heightsByElements);
        setGroupHeight(keys);
        if (keys.length === 0 && onGroupHeightsChange) {
          onGroupHeightsChange();
        }
      });
    },
    [offSet, onGroupHeightsChange, updateGroupHeights]
  );
  const setAllGroupHeights = React__default.useCallback(() => {
    setGroupHeight(groups);
  }, [groups, setGroupHeight]);
  const debouncedUpdate = React__default.useMemo(() => debounce(setAllGroupHeights, 100), [setAllGroupHeights]);
  React__default.useEffect(() => {
    debouncedUpdate();
  }, [debouncedUpdate]);
  React__default.useEffect(() => {
    return () => {
      debouncedUpdate.clear();
    };
  }, [debouncedUpdate]);
  const addElementToGroup = React__default.useCallback(
    (group, element, innerElement) => {
      if (!elementGroups.current[group]) {
        elementGroups.current[group] = [];
      }
      elementGroups.current[group].push({
        element,
        innerElement
      });
    },
    [elementGroups]
  );
  const contextValue = React__default.useMemo(
    () => ({
      addElementToGroup,
      groupHeights,
      triggerUpdate: debouncedUpdate
    }),
    [addElementToGroup, debouncedUpdate, groupHeights]
  );
  return /* @__PURE__ */ React__default.createElement(EqualHeightContext.Provider, { value: contextValue }, children);
};
const findTallestChildHeight = (elements) => {
  return elements.reduce((accumulator, element) => {
    if (element.innerElement) {
      return element.innerElement.scrollHeight > accumulator ? element.innerElement.scrollHeight : accumulator;
    }
    return accumulator;
  }, 0);
};
const getElementGroup = (elementGroups, elementOffSet, offSet) => {
  const keys = Object.keys(elementGroups);
  const existingGroup = keys.find((key) => {
    return Number(key) >= elementOffSet - offSet && Number(key) <= elementOffSet + offSet;
  });
  return existingGroup ? Number(existingGroup) : void 0;
};
const splitElementsByOffset = (elements, offSet) => {
  const elementGroups = {};
  elements.forEach((element) => {
    var _a;
    if (element.element !== null) {
      const offsetTop = offset(element.element).top;
      const elementGroup = (_a = getElementGroup(elementGroups, offsetTop, offSet)) != null ? _a : offsetTop;
      if (!elementGroups[elementGroup]) {
        elementGroups[elementGroup] = [];
      }
      elementGroups[elementGroup].push(element);
    }
  });
  return elementGroups;
};
export {
  EqualHeightContext,
  EqualHeightProvider,
  findTallestChildHeight,
  splitElementsByOffset
};
